import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Star, AlertTriangle, Phone, MessageCircle, X } from 'lucide-react';
import { fmtDateTimeIST } from '../../lib/tz';


const ratingColor = v => v == null ? 'text-[#5C6B64]' : v >= 4 ? 'text-[#2D6A4F]' : v >= 3 ? 'text-[#D4A017]' : 'text-[#C45A41]';
const sentimentLabel = v => v >= 4 ? 'positive' : v === 3 ? 'neutral' : 'negative';
const sentimentBg = v => v >= 4 ? 'bg-[#EAF4EE] text-[#2D6A4F]' : v === 3 ? 'bg-[#FEF6E0] text-[#D4A017]' : 'bg-[#FEF2EE] text-[#C45A41]';

/**
 * Drill-down modal opened when an admin clicks an outlet card on Internal
 * Feedback tab. Loads the full filtered feedback list from
 * `/api/admin/feedback/list` (single source of truth — same endpoint used by
 * the action-required strip on the parent tab) with `sort=negative_first`
 * so action items rise to the top.
 */
export default function OutletFeedbackDrillModal({
  open, onClose, outletId, outletName, startDate, endDate, rangeLabel,
}) {
  const { getHeaders, API } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        outlet_id: outletId,
        sort: 'negative_first',
        limit: '500',
      });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const { data } = await axios.get(`${API}/admin/feedback/list?${params.toString()}`, { headers: getHeaders() });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletId, startDate, endDate]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const negativeCount = items.filter(f => (f.overall_rating || 0) <= 2).length;
  const totalCount = items.length;
  const avg = totalCount > 0
    ? (items.reduce((s, f) => s + (f.overall_rating || 0), 0) / totalCount)
    : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="outlet-feedback-drill-modal"
      >
        <DialogHeader className="px-5 py-3 border-b border-[#E2E8DF] bg-[#F9F9F8] shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-base font-semibold text-[#1A2421] truncate">
                {outletName} — Feedback History
              </DialogTitle>
              <DialogDescription className="text-[11px] text-[#5C6B64] mt-0.5 tabular-nums">
                {rangeLabel || `${startDate} → ${endDate}`} · {totalCount} feedbacks
                {negativeCount > 0 && <span className="text-[#C45A41] font-semibold"> · {negativeCount} action required</span>}
                {avg != null && <span> · avg <span className={`font-semibold ${ratingColor(avg)}`}>{avg.toFixed(2)}★</span></span>}
              </DialogDescription>
            </div>
            <button type="button" onClick={onClose}
              data-testid="outlet-feedback-drill-close"
              className="rounded-full p-1 hover:bg-[#E2E8DF] text-[#5C6B64] hover:text-[#1A2421]">
              <X className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-5 py-3" data-testid="outlet-feedback-drill-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-[#5C6B64] text-center py-12">No feedback in this range.</p>
          ) : (
            <div className="space-y-2">
              {items.map(f => (
                <div key={f.id}
                  className={`p-3 rounded-lg border ${f.action_required ? 'border-[#C45A41]/40 bg-[#FEF2EE]' : 'border-[#E2E8DF] bg-white'}`}
                  data-testid={`drill-feedback-${f.id}`}>
                  <div className="flex items-start justify-between mb-1 gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-base font-bold tabular-nums flex items-center gap-0.5 ${ratingColor(f.overall_rating)}`}>
                        <Star className="w-3.5 h-3.5" fill="currentColor" /> {f.overall_rating}
                      </span>
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{f.form_type}</Badge>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${sentimentBg(f.overall_rating)}`}>
                        {sentimentLabel(f.overall_rating)}
                      </span>
                      {f.action_required && (
                        <Badge variant="destructive" className="text-[9px]">
                          <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                          Action required
                        </Badge>
                      )}
                      {f.name && <span className="text-[11px] text-[#1A2421] font-medium">{f.name}</span>}
                      {f.phone && (
                        <span className="flex items-center gap-1 text-[10px] text-[#5C6B64]">
                          <a href={`tel:+91${f.phone}`} title="Call"
                            data-testid={`drill-call-${f.id}`}
                            className="w-6 h-6 rounded-full bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-white flex items-center justify-center transition-colors">
                            <Phone className="w-3 h-3" />
                          </a>
                          <a href={`https://wa.me/91${f.phone}`} target="_blank" rel="noopener noreferrer" title="WhatsApp"
                            data-testid={`drill-wa-${f.id}`}
                            className="w-6 h-6 rounded-full bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-colors">
                            <MessageCircle className="w-3 h-3" />
                          </a>
                          <span className="font-mono tabular-nums">{f.phone}</span>
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-[#5C6B64] tabular-nums whitespace-nowrap">
                      {fmtDateTimeIST(f.submitted_at)}
                    </span>
                  </div>
                  {f.comments && (
                    <p className="text-xs text-[#1A2421] mt-1 leading-relaxed break-words whitespace-pre-line">
                      "{f.comments}"
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-[10px] text-[#5C6B64] mt-2 flex-wrap">
                    {f.food_rating && <span>Food {f.food_rating}★</span>}
                    {f.service_rating && <span>Service {f.service_rating}★</span>}
                    {f.ambience_rating && <span>Ambience {f.ambience_rating}★</span>}
                    {f.packaging_rating && <span>Packaging {f.packaging_rating}★</span>}
                    {f.delivery_rating && <span>Delivery {f.delivery_rating}★</span>}
                  </div>
                  {Array.isArray(f.custom_responses) && f.custom_responses.length > 0 && (
                    <div className="mt-1.5 pl-3 border-l-2 border-[#E2E8DF] space-y-0.5"
                      data-testid={`drill-custom-${f.id}`}>
                      {f.custom_responses.map((r, i) => (
                        <div key={i} className="text-[10px] leading-tight">
                          <span className="text-[#5C6B64]">{r.question}:</span>{' '}
                          <span className="font-semibold text-[#1A2421]">
                            {r.type === 'rating' ? `${r.answer}★` : String(r.answer)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
